import { Handle } from 'reactflow';


const NODE_THEMES = {
  
  Input:  { headerBg: '#EFF6FF', iconBg: '#BFDBFE', iconColor: '#1D4ED8', icon: 'IN',  handle: '#3B82F6' },
  Output: { headerBg: '#F0FDF4', iconBg: '#BBF7D0', iconColor: '#15803D', icon: 'OUT', handle: '#22C55E' },
  LLM:    { headerBg: '#FAF5FF', iconBg: '#E9D5FF', iconColor: '#7E22CE', icon: 'AI',  handle: '#A855F7' },
  Text:   { headerBg: '#FFF7ED', iconBg: '#FED7AA', iconColor: '#C2410C', icon: 'T',   handle: '#F97316' },

  //custom nodes
  Filter: { headerBg: '#FEFCE8', iconBg: '#FEF08A', iconColor: '#A16207', icon: 'FT',  handle: '#EAB308' },
  Note:   { headerBg: '#F8FAFC', iconBg: '#E2E8F0', iconColor: '#475569', icon: '✎',  handle: '#94A3B8' },
  Timer:  { headerBg: '#FFF1F2', iconBg: '#FECDD3', iconColor: '#BE123C', icon: '⏱',  handle: '#F43F5E' },
  Math: { headerBg: '#EEF2FF', iconBg: '#C7D2FE', iconColor: '#4338CA', icon: '∑', handle: '#6366F1' },
  // API:  { headerBg: '#ECFEFF', iconBg: '#A5F3FC', iconColor: '#0E7490', icon: 'API', handle: '#06B6D4' },
  'API Request': { headerBg: '#ECFEFF', iconBg: '#A5F3FC', iconColor: '#0E7490', icon: 'API', handle: '#06B6D4' },
};
const DEFAULT_THEME = {
  headerBg: 'bg-gray-50', iconBg: 'bg-gray-200',
  iconColor: 'text-gray-700', icon: '?', handle: '#94A3B8',
};

// Field renderers 
const renderField = (field, index) => {
  const { type, options, placeholder, value, onChange, label, rows, min, max, step } = field;

  const labelEl = label && (
    <span className="block text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
      {label}
    </span>
  );

  const inputClass =
    'w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 bg-gray-50 text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors';

  switch (type) {
    case 'text':
      return (
        <div key={index} className="mb-2">
          {labelEl}
          <input
            type="text"
            value={value ?? ''}
            onChange={onChange}
            placeholder={placeholder}
            className={inputClass}
          />
        </div>
      );

    case 'select':
      return (
        <div key={index} className="mb-2">
          {labelEl}
          <select
            value={value ?? ''}
            onChange={onChange}
            className={`${inputClass} cursor-pointer`}
            style={{
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239CA3AF' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
            }}
          >
            {options?.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );

    case 'textarea':
      return (
        <div key={index} className="mb-2">
          {labelEl}
          <textarea
            value={value ?? ''}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows || 3}
            className={`${inputClass} resize-none font-mono leading-relaxed`}
          />
        </div>
      );

    case 'number':
      return (
        <div key={index} className="mb-2">
          {labelEl}
          <input
            type="number"
            value={value ?? ''}
            onChange={onChange}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            className={inputClass}
          />
        </div>
      );

    case 'checkbox':
      return (
        <label key={index} className="flex items-center gap-2 mb-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={onChange}
            className="w-3.5 h-3.5 rounded border-gray-300 text-blue-500 focus:ring-blue-400"
          />
          <span className="text-xs text-gray-600">{label}</span>
        </label>
      );

    default:
      return null;
  }
};

// BaseNode component

export const BaseNode = ({ id, data, config }) => {
  const {
    title = 'Node',
    fields = [],
    handles = [],
    content = null,
    width = 220,
  } = config;

  const theme = NODE_THEMES[title] || DEFAULT_THEME;

  return (
    
    <div
      className="rounded-xl border border-gray-200 bg-white shadow-md hover:shadow-lg transition-shadow duration-200"
      style={{ width }}
    >
    
      {/* ── Colored header ── */}
      <div style={{ background: theme.headerBg }} className="rounded-t-xl px-3 py-2 flex items-center gap-2 border-b border-gray-100">
  <div
    style={{ background: theme.iconBg, color: theme.iconColor, width: 22, height: 22, fontSize: 9, letterSpacing: '0.03em', flexShrink: 0 }}
    className="rounded-md flex items-center justify-center font-bold"
  >
          {theme.icon}
        </div>
        <span className="text-xs font-semibold text-gray-700 tracking-wide">
          {title}
        </span>
      </div>

      {/* ── Body ── */}
      <div className="px-3 pt-2.5 pb-3">
        {content && <div className="mb-2">{content}</div>}
        <div className="space-y-0.5">
          {fields.map((field, index) => renderField(field, index))}
        </div>
      </div>

      {/* ── Handles ── */}
      {handles.map((handle, index) => (
        <Handle
          key={`${handle.id}-${index}`}
          type={handle.type}
          position={handle.position}
          id={handle.id}
          style={{
            width: 11,
            height: 11,
            background: handle.color || theme.handle,
            border: '2px solid white',
            borderRadius: '50%',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
            ...handle.style,
          }}
        />
      ))}
    </div>
  );
};